import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Alert,
  CircularProgress, Stepper, Step, StepLabel, Divider,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { QRCodeSVG } from 'qrcode.react';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';

export default function MfaSetup() {
  const { user, afterMfaSetup, logout } = useAuth();
  const navigate = useNavigate();

  const [activeStep, setActiveStep]   = useState(0);
  const [qrDataUrl, setQrDataUrl]     = useState('');
  const [otpauth, setOtpauth]         = useState('');
  const [secret, setSecret]           = useState('');
  const [code, setCode]               = useState('');
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [starting, setStarting]       = useState(true);

  useEffect(() => {
    api.post('/auth/mfa/setup')
      .then(r => {
        setSecret(r.data.secret);
        setQrDataUrl(r.data.qrDataUrl);
        setOtpauth(r.data.otpauth);
        setStarting(false);
      })
      .catch(e => {
        setError(e.response?.data?.message || 'Failed to generate MFA secret');
        setStarting(false);
      });
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/mfa/confirm', { code });
      afterMfaSetup(data.token);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code - try again');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ maxWidth: 480, width: '100%', p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight={800}>Enable Two-Factor Authentication</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          MFA is required for all admin accounts. Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.) and confirm with a code.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {['Scan QR Code', 'Confirm Code'].map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {starting ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : error && !qrDataUrl ? (
          <Alert severity="error">{error}</Alert>
        ) : activeStep === 0 ? (
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'inline-block', p: 2, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
              <QRCodeSVG value={otpauth} size={200} />
            </Box>
            <Typography variant="caption" display="block" color="text.secondary" mb={1}>
              Can't scan? Enter this key manually:
            </Typography>
            <Typography variant="body2" fontFamily="monospace" fontWeight={700}
              sx={{ backgroundColor: '#f5f5f5', p: 1, borderRadius: 1, mb: 3, wordBreak: 'break-all', fontSize: '0.8rem' }}>
              {secret}
            </Typography>
            <Button variant="contained" fullWidth onClick={() => setActiveStep(1)}>
              Next - Enter Code
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleConfirm}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Open your authenticator app and enter the 6-digit code for <strong>HOA Admin</strong>.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              fullWidth
              label="6-Digit Code"
              value={code}
              autoFocus
              inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              sx={{ mb: 3 }}
            />
            <Button fullWidth type="submit" variant="contained" size="large"
              disabled={loading || code.length < 6}
              sx={{ fontWeight: 700, mb: 1 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Activate MFA'}
            </Button>
            <Button fullWidth variant="text" size="small" color="inherit"
              onClick={() => setActiveStep(0)} sx={{ color: '#6b778c' }}>
              ← Back to QR code
            </Button>
          </form>
        )}

        <Divider sx={{ my: 3 }} />
        <Button fullWidth variant="text" size="small" color="error" onClick={() => logout()}>
          Sign out instead
        </Button>
      </Paper>
    </Box>
  );
}
